import React from 'react';
import { Box, Button, Chip, Typography, LinearProgress, CircularProgress } from '@mui/material';
import { CloudUploadIcon, DeleteForeverIcon, DownloadIcon } from 'assets/constants/Icons'; 
import { getS3PresignedUrl } from 'lib/axiosUtils'; 
import { HTTP_HEADER_KEYS, HTTP_CONTENT_TYPES } from 'assets/constants/apiConstants'; 
import { extractInvoiceNumber } from 'lib/invoiceExtractor';

const SupplierInvoicePanel = ({ invoiceData, onUpdate, isReadOnly }) => {
    const [uploading, setUploading] = React.useState(false);
    const [uploadPct, setUploadPct] = React.useState(0); 
    const [isDragOver, setIsDragOver] = React.useState(false);
    const [isScanning, setIsScanning] = React.useState(false);

    const latestData = React.useRef(invoiceData);
    React.useEffect(() => {
        latestData.current = invoiceData;
    }, [invoiceData]);

    const handleChange = (field, value) => {
        const updated = { ...latestData.current, [field]: value };
        onUpdate(updated); 
    };

    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;

        let detectedInvoiceNo = null;
        if (files[0]) {
            detectedInvoiceNo = await extractInvoiceNumber(files[0], setIsScanning);
        }
        
        setUploading(true);
        const newAttachments = [];
        const totalFiles = files.length;

        try {
            for (let i = 0; i < totalFiles; i++) {
                const file = files[i];

                const pres = await getS3PresignedUrl({}, {
                    fileName: file.name,
                    fileType: file.type || 'application/pdf',
                });
                const { url: putUrl, fileUrl: publicUrl } = pres.data;

                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', putUrl, true);
                    xhr.setRequestHeader(HTTP_HEADER_KEYS.CONTENT_TYPE, file.type || HTTP_CONTENT_TYPES.OCTET_STREAM);
                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable) {
                            const fileProgress = (e.loaded / e.total);
                            const totalProgress = Math.round(((i + fileProgress) / totalFiles) * 100);
                            setUploadPct(totalProgress);
                        }
                    };
                    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject());
                    xhr.onerror = () => reject();
                    xhr.send(file);
                });

                let file_key = '';
                try {
                    const u = new URL(putUrl);
                    const path = decodeURIComponent(u.pathname.replace(/^\/+/, ''));
                    const at = path.indexOf('uploads/');
                    file_key = at >= 0 ? path.substring(at) : path;
                } catch (e) {}

                newAttachments.push({
                    file_name: file.name,
                    file_url: publicUrl,
                    file_key: file_key,
                    uploaded_at: new Date()
                });
            }

            const currentFiles = latestData.current?.invoice_files || [];
            
            const finalUpdates = {
                ...latestData.current,
                invoice_files: [...currentFiles, ...newAttachments]
            };

            if (detectedInvoiceNo) {
                finalUpdates.invoice_number = detectedInvoiceNo;
            }

            onUpdate(finalUpdates);

        } catch (err) {
            console.error('Invoice upload failed', err);
            alert('One or more uploads failed.'); 
        } finally {
            setUploading(false);
            setUploadPct(0);
        }
    };

    const removeFile = (indexToRemove) => {
        const currentFiles = latestData.current?.invoice_files || [];
        const updatedFiles = currentFiles.filter((_, index) => index !== indexToRemove);
        handleChange('invoice_files', updatedFiles);
    };

    const handleDragOver = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!isReadOnly && !uploading && !isScanning) setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragOver(false);
        if (isReadOnly || uploading || isScanning) return;
        const files = e.dataTransfer.files;
        if (files && files.length > 0) handleFileUpload(files);
    };

    const renderField = (label, fieldKey, type = "text", placeholder = "", extraClass = "") => (
        <div className="col-md-6 mb-3">
            {/* <label className="form-label d-block bom-label">{label}</label> */}
            <label className={isReadOnly ? 'readonly-label form-label d-block bom-label' : 'form-label d-block bom-label'}>{label}</label>
            <input
                type={type}
                className={`form-control-customer bom-input ${isReadOnly ? 'readonly-input' : ''} ${extraClass}`}
                value={invoiceData?.[fieldKey] || ''}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
                placeholder={placeholder}
                disabled={isReadOnly}
            />
        </div>
    );

    const filesList = invoiceData?.invoice_files || [];

    return (
        <div className="row mt-4"> 
            <div className="col-12 mt-3 mb-2">
                {/* <div className="bom-divider"></div> */}
                <Typography variant="subtitle2" sx={{ color: '#2d3d42', fontWeight: 600, mb: 1, fontSize: '15px !important' }}>
                    Supplier Invoice & Logistics Details
                </Typography>
            </div>

            {renderField("Invoice Number", "invoice_number", "text", "e.g. INV-001")}
            {renderField("Invoice Value", "invoice_value", "number", "0.00", "no-spin")}
            {renderField("AWB Number", "awb_number", "text", "e.g. 123456")}
            {renderField("Courier Agency", "courier_agency", "text", "e.g. DHL")}

            <div className="col-md-12 mb-3">
                {/* <label className="form-label d-block bom-label">
                    Invoice Attachments
                </label> */}
                <label className={isReadOnly ? 'readonly-label form-label d-block bom-label' : 'form-label d-block bom-label'}>
                    Invoice Attachments
                </label>
                
                {filesList.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {filesList.map((file, index) => (
                            <Chip 
                                key={index}
                                size="small"
                                icon={<CloudUploadIcon />} 
                                label={file.file_name} 
                                onClick={() => window.open(file.file_url, '_blank')}
                                onDelete={!isReadOnly ? () => removeFile(index) : undefined}
                                deleteIcon={<DeleteForeverIcon />}
                                variant="outlined"
                                title={file.file_name}
                                sx={{
                                    maxWidth: 200,
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {!isReadOnly && (
                    <Box sx={{ mt: 0.5 }}>
                        {isScanning ? (
                            <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#fafafa', border: '2px dashed #ccc', borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="textSecondary">Scanning file for Invoice Number...</Typography>
                            </Box>
                        ) : uploading ? (
                            <Box sx={{ width: '100%', display:'flex', alignItems:'center', gap:1 }}>
                                <LinearProgress variant="determinate" value={uploadPct} sx={{flex:1}} />
                                <Typography variant="caption">{uploadPct}%</Typography>
                            </Box>
                        ) : (
                            <Box
                                onDragOver={handleDragOver}
                                onDragEnter={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                sx={{
                                    border: '2px dashed',
                                    borderColor: isDragOver ? '#2d3d42' : '#ccc',
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: 'center',
                                    backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                                    transition: 'all 0.2s',
                                    cursor: 'default',
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    gap: 1
                                }}
                            >
                                <Typography variant="body2" color="textSecondary" sx={{fontSize: '12px'}}>
                                    Drag & drop files here, or
                                </Typography>
                                
                                <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ textTransform: 'none', borderColor: '#ccc', color: '#555', bgcolor: 'white' }}
                                    disabled={uploading}
                                >
                                    Browse Files
                                    <input 
                                        type="file" 
                                        hidden 
                                        multiple 
                                        accept="application/pdf,image/*" 
                                        onChange={(e) => handleFileUpload(e.target.files)} 
                                    />
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </div>
        </div>
    );
};

export default SupplierInvoicePanel;